import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
// @ts-expect-error
import { keyResponder, onKey } from 'ember-keyboard';
import { Input } from '@ember/component';
import { debounce } from '@ember/runloop';
import { eq, or, and, not } from 'ember-truth-helpers';
import { concat } from '@ember/helper';
import autofocus from '../modifiers/autofocus';
import { formatDate } from '../helpers/format-date';

import type CommandPaletteService from '../services/command-palette';
import type { Command } from '../services/command-palette';
import type SearchService from '../services/search';
import type { SearchResult, SearchRequest } from '../types/search';

interface CommandPaletteSignature {
  Args: {
    isOpen: boolean;
    close: (close: boolean) => void;
  };
}

@keyResponder({ priority: 2 })
export default class CommandPaletteComponent extends Component<CommandPaletteSignature> {
  @service declare commandPalette: CommandPaletteService;
  @service declare search: SearchService;

  @tracked query = '';
  @tracked filteredCommands: Command[] = [];
  @tracked searchResults: SearchResult[] = [];
  @tracked selectedIndex = -1;
  @tracked isLoading = false;
  @tracked searchError: string | null = null;

  constructor(owner: any, args: any) {
    super(owner, args);
    this.updateFilteredCommands();
  }

  get isOpen() {
    return this.args.isOpen ?? false;
  }

  get showRecentCommands() {
    return this.query.length === 0;
  }

  get recentCommands() {
    return this.commandPalette.getRecentCommands();
  }

  get defaultActions() {
    // Show default actions when no query (Home, eSports, News)
    return this.commandPalette.getDefaultActions();
  }

  get displayCommands() {
    if (this.showRecentCommands) {
      // Show both default actions and recent commands when no query
      const recentCommands = this.recentCommands;
      if (recentCommands.length > 0) {
        return this.defaultActions;
      }
      // If no recent commands, just show default actions
      return this.defaultActions;
    }
    return this.filteredCommands;
  }

  get hasSearchResults() {
    return this.searchResults.length > 0;
  }

  get totalResults() {
    return this.displayCommands.length + this.searchResults.length;
  }

  get hasCommands() {
    return this.totalResults > 0;
  }

  // Create indexed search results with adjusted indices
  get indexedSearchResults() {
    return this.searchResults.map((result, index) => ({
      ...result,
      adjustedIndex: this.displayCommands.length + index,
      originalIndex: index,
    }));
  }

  get emptyStateMessage() {
    if (this.showRecentCommands) {
      return 'No recent commands. Start typing to search for commands.';
    }
    return `No commands found for "${this.query}".`;
  }

  // Keyboard handling
  @onKey('w')
  @onKey('shift+w')
  @onKey('s')
  @onKey('shift+s')
  noOp(e: any, ek: any) {
    ek.stopPropagation();
    ek.stopImmediatePropagation();
  }

  @onKey('Escape')
  closeCommandPalette() {
    this.args.close(false);
  }

  @onKey('ArrowDown')
  selectNext(e: any, ek: any) {
    if (ek && typeof ek.preventDefault === 'function') {
      ek.preventDefault();
    } else if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (this.totalResults > 0) {
      this.selectedIndex = Math.min(this.selectedIndex + 1, this.totalResults - 1);
      this.scrollToSelected();
    }
  }

  @onKey('ArrowUp')
  selectPrevious(e: any, ek: any) {
    if (ek && typeof ek.preventDefault === 'function') {
      ek.preventDefault();
    } else if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (this.totalResults > 0) {
      this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
      this.scrollToSelected();
    }
  }

  @onKey('Enter')
  async executeSelectedCommand(e: any, ek: any) {
    if (ek && typeof ek.preventDefault === 'function') {
      ek.preventDefault();
    } else if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    if (this.selectedIndex >= 0) {
      // Check if selected index is a command
      if (this.selectedIndex < this.displayCommands.length) {
        const command = this.displayCommands[this.selectedIndex];
        await this.executeCommand(command);
      }
      // Check if selected index is a search result
      else if (this.selectedIndex < this.totalResults) {
        const searchResultIndex = this.selectedIndex - this.displayCommands.length;
        const searchResult = this.searchResults[searchResultIndex];
        if (searchResult) {
          this.handleSearchResultClick(searchResult, searchResultIndex);
        }
      }
    }
  }

  @action
  updateQuery(event: Event) {
    const target = event.target as HTMLInputElement;
    this.query = target.value;
    this.selectedIndex = -1;

    // Debounced command filtering
    debounce(this, this.updateFilteredCommands, 150);
  }

  @action
  async updateFilteredCommands() {
    this.isLoading = true;
    this.searchError = null;

    try {
      // Search commands first
      this.filteredCommands = this.commandPalette.searchCommands(this.query);

      // If we have a query, also search content
      if (this.query.length >= 2) {
        const searchRequest: SearchRequest = {
          query: this.query,
          limit: 15, // More results for better command palette experience
          includeHighlights: true,
        };

        const searchResponse = await this.search.search(searchRequest, {
          enableCache: true,
          enableAnalytics: false, // Don't track analytics for command palette searches
        });

        this.searchResults = searchResponse.results;
      } else {
        this.searchResults = [];
      }
    } catch (error) {
      console.warn('Search error in command palette:', error);
      this.searchError = 'Search temporarily unavailable';
      this.searchResults = [];
    } finally {
      this.isLoading = false;
    }
  }

  @action
  selectCommand(command: Command, index: number) {
    this.selectedIndex = index;
  }

  @action
  selectSearchResult(result: SearchResult, index: number) {
    // Adjust index to account for commands that come before search results
    const adjustedIndex = this.displayCommands.length + index;
    this.selectedIndex = adjustedIndex;
  }

  @action
  async executeCommand(command: Command) {
    if (this.commandPalette.isExecuting) return;

    // Close the command palette
    this.args.close(false);

    // Execute the command
    await this.commandPalette.executeCommand(command);

    // Reset component state
    this.query = '';
    this.filteredCommands = [];
    this.searchResults = [];
    this.selectedIndex = -1;
  }

  @action
  handleSearchResultClick(result: SearchResult, index: number) {
    // Track the click for analytics
    this.search.trackResultClick(this.query, result, index);

    // Close the command palette
    this.args.close(false);

    // Navigate to the search result URL
    if (result.url) {
      window.location.href = result.url;
    }

    // Reset component state
    this.query = '';
    this.filteredCommands = [];
    this.searchResults = [];
    this.selectedIndex = -1;
  }

  @action
  handleFormSubmit(event: Event) {
    event.preventDefault();

    if (this.selectedIndex >= 0) {
      // Check if selected index is a command
      if (this.selectedIndex < this.displayCommands.length) {
        const command = this.displayCommands[this.selectedIndex];
        this.executeCommand(command);
      }
      // Check if selected index is a search result
      else if (this.selectedIndex < this.totalResults) {
        const searchResultIndex = this.selectedIndex - this.displayCommands.length;
        const searchResult = this.searchResults[searchResultIndex];
        if (searchResult) {
          this.handleSearchResultClick(searchResult, searchResultIndex);
        }
      }
    }
  }

  @action
  clearRecentCommands() {
    this.commandPalette.clearRecentCommands();
  }

  @action
  handlePaletteClick(event: Event) {
    // Prevent clicks on the palette itself from closing it
    event.stopPropagation();
  }

  @action
  handleOverlayClick() {
    // Close when clicking the overlay backdrop
    this.args.close(false);
  }

  // Scroll the selected item into view
  private scrollToSelected() {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      let selectedElement: Element | null = null;
      
      if (this.selectedIndex < this.displayCommands.length) {
        // Selected item is a command
        selectedElement = document.getElementById(`command-${this.selectedIndex}`);
      } else if (this.selectedIndex >= this.displayCommands.length) {
        // Selected item is a search result
        const searchResultIndex = this.selectedIndex - this.displayCommands.length;
        selectedElement = document.getElementById(`search-result-${searchResultIndex}`);
      }
      
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 0);
  }

  <template>
    {{#if this.isOpen}}
      <div class='command-palette-overlay' role='dialog' aria-label='Command Palette' {{on 'click' this.handleOverlayClick}}>
        <div class='command-palette' {{on 'click' this.handlePaletteClick}}>
          <div class='command-palette-header'>
            <div class='search-input-wrapper'>
              <svg class='search-icon' width='20' height='20' viewBox='0 0 20 20'>
                <path d='M14.386 14.386l4.0877 4.0877-4.0877-4.0877c-2.9418 2.9419-7.7115 2.9419-10.6533 0-2.9419-2.9418-2.9419-7.7115 0-10.6533 2.9418-2.9419 7.7115-2.9419 10.6533 0 2.9419 2.9418 2.9419 7.7115 0 10.6533z' stroke='currentColor' fill='none' fill-rule='evenodd' stroke-linecap='round' stroke-linejoin='round'></path>
              </svg>
              
              <form {{on 'submit' this.handleFormSubmit}}>
                <Input
                  class='search-input'
                  placeholder='Type a command or search...'
                  @type='text'
                  @value={{this.query}}
                  {{on 'input' this.updateQuery}}
                  {{autofocus}}
                  autocomplete='off'
                  spellcheck='false'
                  aria-expanded={{if this.hasCommands 'true' 'false'}}
                  aria-haspopup='listbox'
                  aria-owns='command-list'
                  role='combobox'
                  aria-activedescendant={{if (eq this.selectedIndex -1) '' (concat 'command-' this.selectedIndex)}}
                />
              </form>
            </div>
          </div>

          <div class='command-palette-content'>
            {{#if this.isLoading}}
              <div class='loading-state'>
                <div class='loading-spinner'></div>
                <span>Searching commands...</span>
              </div>
            {{else if this.hasCommands}}
              {{#if this.displayCommands.length}}
                <div class='command-palette-section'>
                  {{#if this.showRecentCommands}}
                    <div class='section-header'>
                      <h3 class='section-title'>Recent Commands</h3>
                      <button 
                        type='button' 
                        class='clear-recents-button' 
                        {{on 'click' this.clearRecentCommands}}
                        title='Clear recent commands'
                      >
                        Clear
                      </button>
                    </div>
                  {{else}}
                    <div class='section-header'>
                      <h3 class='section-title'>Commands</h3>
                    </div>
                  {{/if}}

                  <div class='results-list' id='command-list' role='listbox'>
                    {{#each this.displayCommands as |command index|}}
                      <button
                        type='button'
                        class='result-item {{if (eq index this.selectedIndex) "selected"}}'
                        role='option'
                        id='command-{{index}}'
                        aria-selected={{if (eq index this.selectedIndex) 'true' 'false'}}
                        {{on 'click' (fn this.executeCommand command)}}
                        {{on 'mouseenter' (fn this.selectCommand command index)}}
                      >
                        <div class='result-icon'>
                          {{#if command.icon}}
                            <svg class='command-icon'>
                              <use href='#svg-{{command.icon}}'></use>
                            </svg>
                          {{else}}
                            <svg class='command-icon' width='16' height='16' viewBox='0 0 16 16'>
                              <circle cx='8' cy='8' r='2' fill='currentColor'></circle>
                            </svg>
                          {{/if}}
                        </div>
                        
                        <div class='result-content'>
                          <div class='result-title'>
                            {{command.title}}
                          </div>
                          {{#if command.description}}
                            <div class='result-description'>
                              {{command.description}}
                            </div>
                          {{/if}}
                        </div>

                        <div class='result-meta'>
                          {{#if command.category}}
                            <span class='result-category'>{{command.category}}</span>
                          {{/if}}
                          {{#if command.shortcut}}
                            <kbd class='shortcut-kbd'>{{command.shortcut}}</kbd>
                          {{/if}}
                        </div>
                      </button>
                    {{/each}}
                  </div>
                </div>
              {{/if}}

              {{#if this.hasSearchResults}}
                <div class='command-palette-section'>
                  <div class='section-header'>
                    <h3 class='section-title'>Search Results</h3>
                  </div>

                  <div class='results-list'>
                    {{#each this.indexedSearchResults as |result|}}
                      <button
                        type='button'
                        class='result-item {{if (eq result.adjustedIndex this.selectedIndex) "selected"}}'
                        role='option'
                        id='search-result-{{result.originalIndex}}'
                        aria-selected={{if (eq result.adjustedIndex this.selectedIndex) 'true' 'false'}}
                        {{on 'click' (fn this.handleSearchResultClick result result.originalIndex)}}
                        {{on 'mouseenter' (fn this.selectSearchResult result result.originalIndex)}}
                      >
                          <div class='result-icon'>
                            {{#if (eq result.type 'article')}}
                              <svg class='command-icon' width='16' height='16' viewBox='0 0 16 16'>
                                <path d='M14.5 2h-13C.67 2 0 2.67 0 3.5v9c0 .83.67 1.5 1.5 1.5h13c.83 0 1.5-.67 1.5-1.5v-9c0-.83-.67-1.5-1.5-1.5zM1.5 3h13c.28 0 .5.22.5.5v9c0 .28-.22.5-.5.5h-13c-.28 0-.5-.22-.5-.5v-9c0-.28.22-.5.5-.5z' fill='currentColor'/>
                                <path d='M3 5h10v1H3V5zM3 7h10v1H3V7zM3 9h7v1H3V9z' fill='currentColor'/>
                              </svg>
                            {{else if (eq result.type 'team')}}
                              <svg class='command-icon' width='16' height='16' viewBox='0 0 16 16'>
                                <path d='M5.5 3A2.5 2.5 0 0 0 3 5.5v7A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5v-7A2.5 2.5 0 0 0 10.5 3h-5zM4 5.5A1.5 1.5 0 0 1 5.5 4h5A1.5 1.5 0 0 1 12 5.5v7a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-7z' fill='currentColor'/>
                              </svg>
                            {{else if (eq result.type 'player')}}
                              <svg class='command-icon' width='16' height='16' viewBox='0 0 16 16'>
                                <path d='M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z' fill='currentColor'/>
                              </svg>
                            {{else if (eq result.type 'tournament')}}
                              <svg class='command-icon' width='16' height='16' viewBox='0 0 16 16'>
                                <path d='M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5c0 .538-.012 1.05-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33.076 33.076 0 0 1 2.5.5zm.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935zm10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935z' fill='currentColor'/>
                              </svg>
                            {{else}}
                              <svg class='command-icon' width='16' height='16' viewBox='0 0 16 16'>
                                <circle cx='8' cy='8' r='2' fill='currentColor'></circle>
                              </svg>
                            {{/if}}
                          </div>
                          
                          <div class='result-content'>
                            <div class='result-title'>
                              {{{result.highlightedFields.title}}}
                            </div>
                            {{#if result.excerpt}}
                              <div class='result-excerpt'>
                                {{{result.highlightedFields.excerpt}}}
                              </div>
                            {{/if}}
                            {{#if result.author}}
                              <div class='result-meta'>
                                <span class='result-author'>{{result.author}}</span>
                                {{#if result.publishedDate}}
                                  <span class='result-separator'>•</span>
                                  <span class='result-date'>{{formatDate result.publishedDate}}</span>
                                {{/if}}
                              </div>
                            {{/if}}
                          </div>

                          <div class='result-meta'>
                            <span class='result-type'>{{result.type}}</span>
                            <svg class='result-arrow' width='16' height='16' viewBox='0 0 16 16'>
                              <path d='M8.636 3.5a.5.5 0 0 0-.5.5v1.5H2.5a.5.5 0 0 0 0 1h5.636V8a.5.5 0 0 0 .854.354l2.5-2.5a.5.5 0 0 0 0-.708l-2.5-2.5A.5.5 0 0 0 8.636 3.5z' fill='currentColor'/>
                            </svg>
                          </div>
                        </button>
                    {{/each}}
                  </div>
                </div>
              {{/if}}
            {{else}}
              <div class='empty-state'>
                <svg class='empty-icon' width='32' height='32' viewBox='0 0 24 24'>
                  <path d='M10 18a7.952 7.952 0 004.897-1.688l4.396 4.396 1.414-1.414-4.396-4.396A7.952 7.952 0 0018 10c0-4.411-3.589-8-8-8s-8 3.589-8 8 3.589 8 8 8zM10 4c3.309 0 6 2.691 6 6s-2.691 6-6 6-6-2.691-6-6 2.691-6 6-6z' fill='currentColor'></path>
                </svg>
                <div class='empty-text'>
                  {{this.emptyStateMessage}}
                </div>
              </div>
            {{/if}}
          </div>
        </div>
      </div>
    {{/if}}
  </template>
}
